import React, { useEffect, useState } from 'react';
import {
  Space,
  Input,
  Button,
  Form,
  Typography,
  Divider,
  Tooltip,
  Switch,
  Row,
  Col,
  Select,
} from 'antd';
import axios from '../shared/axios';
import { BarChart, CartesianGrid, XAxis, YAxis, Bar } from 'recharts';
import { useParams } from 'react-router-dom';
import { useForm } from 'antd/lib/form/Form';
import { formatFeedbackCSVData } from '../../utilities/formatData';
import { CSVLink } from 'react-csv';
import TableWithSorter from '../Common/TableWithSorter';

const { Title } = Typography;
const { Option } = Select;

const FeedbackPage = () => {
  const [unitOfferId, setUnitOfferId] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [ratingsChartData, setRatingsChartData] = useState([]);
  const [tableData, setTableData] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(true);
  const [fullAnswerData, setFullAnswerData] = useState([]);
  const [feedbackCSVData, setFeedbackCSVData] = useState({});
  const [filterForm] = useForm();
  const [questions, setQuestions] = useState([]);

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      getFeedback(id);
      setUnitOfferId(id);
    }
  }, [id]);

  let formatData = (data, survey) => {
    let adminColumns = () => [
      {
        title: 'Learner Id',
        dataIndex: 'learnerId',
        key: 'learnerId',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Answer',
        dataIndex: 'answer',
        key: 'answer',
      },
      {
        title: 'CreatorRating',
        dataIndex: 'rating',
        key: 'rating',
      },
    ];

    let trainerColumns = () => [
      {
        title: 'Answer',
        dataIndex: 'answer',
        key: 'answer',
      },
      {
        title: 'CreatorRating',
        dataIndex: 'rating',
        key: 'rating',
      },
    ];

    const ratingQuestionId = Object.keys(data[0].questions).find(
      (questionId) => data[0].questions[questionId].type === 'rating'
    );

    let ratingsInfo = {};

    let tableDataFromAPI = {};
    data.map((learner) => {
      //Iterate over the question data
      for (const [key, value] of Object.entries(learner.questions)) {
        if (value.type === 'rating') {
          const rating = value.answer * 10;

          if (!ratingsInfo[key]) {
            let ratingsChartStats = new Array(10);
            for (let i = 0; i < 10; i++) {
              ratingsChartStats[i] = {
                name: `${i + 1}⭐`,
                numberOfReviews: 0,
              };
            }

            ratingsInfo[key] = {
              questionLabel: value.question,
              feedbackCount: 0,
              totalSumOfFeedback: 0,
              feedbackAvg: 0,
              chartStats: ratingsChartStats,
            };
          }

          ratingsInfo[key].chartStats[rating - 1].numberOfReviews++;
          ratingsInfo[key].feedbackCount = ratingsInfo[key].feedbackCount + 1;
          ratingsInfo[key].totalSumOfFeedback =
            ratingsInfo[key].totalSumOfFeedback + rating;
          ratingsInfo[key].feedbackAvg = (
            ratingsInfo[key].totalSumOfFeedback / ratingsInfo[key].feedbackCount
          ).toFixed(2);
        } else {
          if (!tableDataFromAPI[key]) {
            tableDataFromAPI[key] = {
              label: value.question,
              adminColumns: adminColumns(),
              trainerColumns: trainerColumns(),
              data: [],
            };
          }
          tableDataFromAPI[key].data.push({
            key: value._id,
            name:
              learner.learnerData.firstName +
              ' ' +
              learner.learnerData.lastName,
            email: learner.learnerData.email,
            answer: value.answer,
            learnerId: learner.learnerData.learnerId,
            rating: learner.questions[ratingQuestionId].answer * 10,
          });
        }
      }
    });

    setRatingsChartData(Object.values(ratingsInfo));

    setTableData({
      isAvailable: true,
      data: tableDataFromAPI,
    });

    const averageRating = ratingsInfo[ratingQuestionId].feedbackAvg;

    setFeedbackCSVData(
      formatFeedbackCSVData(
        tableDataFromAPI,
        averageRating,
        survey.courseOfferInfo?.code,
        survey.unitOfferInfo?.unitDescription
      )
    );
  };

  let createTableJSX = (data) => {
    let tableJSX = [];
    for (const [questionId, questionInfo] of Object.entries(data)) {
      tableJSX.push(
        <section>
          <Divider>
            <Title level={4}>{questionInfo.label}</Title>
          </Divider>
          <TableWithSorter
            dataSource={questionInfo.data}
            columns={
              visibleColumns
                ? questionInfo.trainerColumns
                : questionInfo.adminColumns
            }
            pagination={{
              pageSize: 30,
            }}
          />
        </section>
      );
    }
    return tableJSX;
  };

  let getFeedback = (id) => {
    let FeedbackURL = `${process.env.REACT_APP_BACKEND_URL}/feedback-answers`;
    let params;
    if (!!id) {
      params = {
        unitOfferId: id,
      };
    } else {
      alert('Enter a valid courseOfferID or UnitOfferId');
      return;
    }
    axios
      .get(FeedbackURL, {
        params,
      })
      .then((response) => {
        const surveyQuestions = response.data.answerInfo[0]?.questions
          ? Object.keys(response.data.answerInfo[0]?.questions).map((qnId) => {
              return {
                questionId: qnId,
                question: response.data.answerInfo[0]?.questions[qnId].question,
                type: response.data.answerInfo[0]?.questions[qnId].type,
              };
            })
          : [];
        setFullAnswerData(response.data.answerInfo);
        setQuestions(surveyQuestions);
        formatData(response.data.answerInfo, response.data);
        console.log(response.data.answerInfo);
        setShowFeedback(true);
      })
      .catch((error) => {
        console.log('Error getting data from the admin backend:', error);
        alert('Getting data from the backend failed!');
        setShowFeedback(false);
      });
  };

  const onSearch = (data) => {
    const qnId = data.param;
    let answer = data.val;
    const filteredAnswer = fullAnswerData.filter((item) => {
      if (
        item.questions[qnId].type === 'rating' ||
        item.questions[qnId].type === 'number'
      ) {
        answer = parseFloat(answer);
      }
      return item.questions[qnId].answer === answer;
    });
    if (filteredAnswer.length === 0) {
      alert('Answer does not exist with this question');
      return;
    }
    formatData(filteredAnswer);
  };
  const onReset = () => {
    filterForm.resetFields();
    formatData(fullAnswerData);
  };
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Title level={2}>Get Feedback</Title>
        <Form onFinish={() => getFeedback(unitOfferId)}>
          <Space>
            <Form.Item name="unitOfferId" label=" Unit Offer ID" required>
              <Input
                defaultValue={id}
                placeholder="Enter the Unit Offer ID"
                onChange={(e) => setUnitOfferId(e.target.value)}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Get Feedback
              </Button>
              {feedbackCSVData.data && feedbackCSVData.data.length > 0 ? (
                <Button type="primary" style={{ marginLeft: '10px' }}>
                  <CSVLink
                    data={feedbackCSVData.data}
                    headers={feedbackCSVData.headers}
                    filename={feedbackCSVData.filename}
                  >
                    Export to CSV
                  </CSVLink>
                </Button>
              ) : null}
            </Form.Item>
          </Space>
        </Form>
        <Divider />
        {showFeedback ? (
          <>
            <section className="grey-section">
              <Title level={4}>Filter By Question</Title>
              <Form form={filterForm} onFinish={onSearch}>
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item name="param" rules={[{ required: true }]}>
                      <Select placeholder="Search by...">
                        {questions.map((question) => {
                          return (
                            <Option value={question.questionId}>
                              {question.question}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row span={16}>
                  <Col span={16}>
                    <Form.Item name="val" rules={[{ required: true }]}>
                      <Input placeholder="Enter the answer you want to search by..." />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item>
                      <Button type="primary" htmlType="submit">
                        Search
                      </Button>
                      <Button htmlType="button" onClick={onReset}>
                        Reset
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </section>

            <section>
              {ratingsChartData.map(
                ({ questionLabel, feedbackAvg, chartStats }, key) => {
                  const label = `${questionLabel}: ${feedbackAvg}⭐`;

                  return (
                    <section
                      key={key}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Divider>
                        <Title level={4}>{label}</Title>
                      </Divider>
                      <BarChart width={730} height={500} data={chartStats}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="numberOfReviews" fill="#8884d8" />
                      </BarChart>
                    </section>
                  );
                }
              )}
            </section>

            <Switch
              checkedChildren="Admin View"
              unCheckedChildren="Trainer View"
              defaultChecked={false}
              onChange={() => setVisibleColumns((prev) => !prev)}
            />

            {tableData.isAvailable ? (
              createTableJSX(tableData.data)
            ) : (
              <div>Run query above</div>
            )}
          </>
        ) : (
          <div>Run query above</div>
        )}
      </div>
    </>
  );
};

export default FeedbackPage;
